import { useEffect, useRef, useState } from 'react';
import { CheckCheck, FileSearch, FileText, FolderOpen, Loader2, MessageSquare, Search, Send, SquarePen, Square, X } from 'lucide-react';

const SESSION_MESSAGES_KEY = 'aco_rag_messages';
const SESSION_CONV_ID_KEY  = 'aco_rag_conv_id';

const BRAND = '#0BBCC1';
const API_BASE = window.__RUNTIME_CONFIG__?.API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface ToolStep {
  id: string;
  name: string;
  args: Record<string, unknown>;
  done: boolean;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  citations?: string[];
  streaming?: boolean;
  toolSteps?: ToolStep[];
}

type ToolMeta = { label: string; Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>; argKey: string };
const TOOL_META: Record<string, ToolMeta> = {
  semantic_search: { label: 'Recherche',   Icon: Search,     argKey: 'query'   },
  text_search:     { label: 'Texte',       Icon: FileSearch, argKey: 'query'   },
  read:            { label: 'Lecture',     Icon: FileText,   argKey: 'path'    },
  list:            { label: 'Navigation',  Icon: FolderOpen, argKey: 'path'    },
  find_files:      { label: 'Fichiers',    Icon: FileSearch, argKey: 'pattern' },
};

// ── SSE Parsing ───────────────────────────────────────────────────────────────

type SSEEvent =
  | { type: 'content'; text: string }
  | { type: 'citations'; sources: string[] }
  | { type: 'conversation_id'; id: number }
  | { type: 'tool'; id: string; name: string; args: Record<string, unknown> }
  | { type: 'tool_end'; id: string }
  | { type: 'error'; message: string }
  | { type: 'other' };

function parseSSELine(line: string): SSEEvent {
  if (!line.startsWith('data:')) return { type: 'other' };
  const raw = line.replace(/^data:\s?/, '').trim();
  if (!raw || raw === '[DONE]') return { type: 'other' };
  try {
    const ev = JSON.parse(raw) as Record<string, unknown>;
    if (typeof ev.conversation_id === 'number') return { type: 'conversation_id', id: ev.conversation_id };
    if (typeof ev.content === 'string') return { type: 'content', text: ev.content };
    if (Array.isArray(ev.citations)) return { type: 'citations', sources: ev.citations as string[] };
    if (ev.tool && typeof ev.tool === 'object') {
      const t = ev.tool as Record<string, unknown>;
      return { type: 'tool', id: String(t.id ?? ''), name: String(t.name ?? ''), args: (t.args as Record<string, unknown>) ?? {} };
    }
    if (ev.tool_end && typeof ev.tool_end === 'object') {
      return { type: 'tool_end', id: String((ev.tool_end as Record<string, unknown>).id ?? '') };
    }
    if (typeof ev.error === 'string') return { type: 'error', message: ev.error };
  } catch { /* non-JSON line, ignore */ }
  return { type: 'other' };
}

// ── Inline Markdown (bold, italic, inline code, links) ────────────────────────

function renderInline(text: string): React.ReactNode {
  type Pattern = [RegExp, (m: RegExpMatchArray, k: number) => React.ReactNode];
  const patterns: Pattern[] = [
    [/\*\*([^*]+)\*\*/, (m, k) => <strong key={k} style={{ color: 'rgba(255,255,255,0.95)', fontWeight: 600 }}>{m[1]}</strong>],
    [/__([^_]+)__/, (m, k) => <strong key={k} style={{ fontWeight: 600 }}>{m[1]}</strong>],
    [/\*([^*]+)\*/, (m, k) => <em key={k} style={{ color: 'rgba(255,255,255,0.75)' }}>{m[1]}</em>],
    [/`([^`]+)`/, (m, k) => (
      <code key={k} style={{
        background: 'rgba(11,188,193,0.15)',
        color: BRAND,
        padding: '1px 5px',
        borderRadius: '4px',
        fontSize: '0.82em',
        fontFamily: 'monospace',
      }}>{m[1]}</code>
    )],
    [/\[([^\]]+)\]\(([^)]+)\)/, (m, k) => (
      <a key={k} href={m[2]} target="_blank" rel="noopener noreferrer"
        style={{ color: BRAND, textDecoration: 'underline', textUnderlineOffset: '2px' }}>{m[1]}</a>
    )],
  ];

  const parts: React.ReactNode[] = [];
  let remaining = text;
  let k = 0;

  while (remaining) {
    let earliest: { index: number; end: number; node: React.ReactNode } | null = null;

    for (const [regex, render] of patterns) {
      const match = remaining.match(regex);
      if (match && match.index !== undefined) {
        const end = match.index + match[0].length;
        if (!earliest || match.index < earliest.index) {
          earliest = { index: match.index, end, node: render(match, k++) };
        }
      }
    }

    if (!earliest) {
      parts.push(remaining);
      break;
    }
    if (earliest.index > 0) parts.push(remaining.slice(0, earliest.index));
    parts.push(earliest.node);
    remaining = remaining.slice(earliest.end);
  }

  return <>{parts}</>;
}

// ── Block Markdown Renderer ───────────────────────────────────────────────────

function MarkdownContent({ content, streaming }: { content: string; streaming?: boolean }) {
  const nodes: React.ReactNode[] = [];
  const lines = content.split('\n');
  let i = 0;
  let k = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.trimStart().startsWith('```')) {
      const lang = line.trimStart().slice(3).trim();
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      nodes.push(
        <pre key={k++} style={{
          background: 'rgba(0,0,0,0.4)',
          border: '1px solid rgba(11,188,193,0.18)',
          borderRadius: '8px',
          padding: '10px 12px',
          margin: '6px 0',
          overflowX: 'auto',
          fontSize: '0.78em',
          lineHeight: 1.55,
          fontFamily: 'monospace',
          color: 'rgba(255,255,255,0.85)',
        }}>
          {lang && (
            <div style={{
              color: BRAND,
              fontSize: '0.7em',
              marginBottom: '6px',
              opacity: 0.6,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}>{lang}</div>
          )}
          <code>{codeLines.join('\n')}</code>
        </pre>
      );
      i++; // skip closing ```
      continue;
    }

    // Headings
    if (line.startsWith('### ')) {
      nodes.push(
        <div key={k++} style={{ fontWeight: 600, fontSize: '0.9em', color: 'rgba(255,255,255,0.95)', marginTop: '10px', marginBottom: '2px' }}>
          {renderInline(line.slice(4))}
        </div>
      );
      i++; continue;
    }
    if (line.startsWith('## ')) {
      nodes.push(
        <div key={k++} style={{ fontWeight: 700, fontSize: '0.95em', color: 'rgba(255,255,255,0.95)', marginTop: '12px', marginBottom: '3px' }}>
          {renderInline(line.slice(3))}
        </div>
      );
      i++; continue;
    }
    if (line.startsWith('# ')) {
      nodes.push(
        <div key={k++} style={{ fontWeight: 700, fontSize: '1em', color: BRAND, marginTop: '12px', marginBottom: '4px' }}>
          {renderInline(line.slice(2))}
        </div>
      );
      i++; continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim()) || /^\*\*\*+$/.test(line.trim())) {
      nodes.push(<hr key={k++} style={{ border: 'none', borderTop: `1px solid ${BRAND}30`, margin: '8px 0' }} />);
      i++; continue;
    }

    // Unordered list
    if (/^[-*+]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*+]\s/.test(lines[i])) {
        items.push(lines[i].slice(2));
        i++;
      }
      nodes.push(
        <ul key={k++} style={{ margin: '4px 0', paddingLeft: 0, listStyle: 'none' }}>
          {items.map((item, j) => (
            <li key={j} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start', marginBottom: '3px' }}>
              <span style={{ color: BRAND, marginTop: '1px', flexShrink: 0, lineHeight: 1.55 }}>›</span>
              <span style={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.55 }}>{renderInline(item)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(line)) {
      const items: { num: string; text: string }[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        const m = lines[i].match(/^(\d+)\.\s(.*)/)!;
        items.push({ num: m[1], text: m[2] });
        i++;
      }
      nodes.push(
        <ol key={k++} style={{ margin: '4px 0', paddingLeft: 0, listStyle: 'none' }}>
          {items.map((item, j) => (
            <li key={j} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '3px' }}>
              <span style={{ color: BRAND, fontWeight: 600, minWidth: '18px', flexShrink: 0, lineHeight: 1.55 }}>{item.num}.</span>
              <span style={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.55 }}>{renderInline(item.text)}</span>
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // Empty line → spacer
    if (!line.trim()) {
      if (nodes.length > 0) nodes.push(<div key={k++} style={{ height: '5px' }} />);
      i++; continue;
    }

    // Paragraph
    nodes.push(
      <p key={k++} style={{ margin: '2px 0', color: 'rgba(255,255,255,0.82)', lineHeight: 1.6 }}>
        {renderInline(line)}
      </p>
    );
    i++;
  }

  return (
    <div style={{ fontSize: '0.875rem' }}>
      {nodes}
      {streaming && (
        <span style={{
          display: 'inline-block',
          width: '7px',
          height: '14px',
          background: BRAND,
          marginLeft: '2px',
          verticalAlign: 'text-bottom',
          animation: 'caret-blink 1s steps(1) infinite',
          borderRadius: '1px',
        }} />
      )}
    </div>
  );
}

// ── Tool Steps Panel ──────────────────────────────────────────────────────────

function ToolStepsPanel({ steps }: { steps: ToolStep[] }) {
  if (!steps.length) return null;
  return (
    <div style={{ marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
      {steps.map(step => {
        const meta = TOOL_META[step.name];
        const Icon = meta?.Icon;
        const target = meta?.argKey ? String(step.args[meta.argKey] ?? '') : '';
        const truncated = target.length > 38 ? '…' + target.slice(-36) : target;
        return (
          <div key={step.id} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 8px',
            background: step.done ? 'rgba(11,188,193,0.04)' : 'rgba(11,188,193,0.08)',
            border: `1px solid rgba(11,188,193,${step.done ? '0.1' : '0.22'})`,
            borderRadius: '6px',
            transition: 'all 0.2s',
          }}>
            {/* Status */}
            {step.done
              ? <CheckCheck className="w-3 h-3" style={{ color: BRAND, flexShrink: 0, opacity: 0.7 }} />
              : <Loader2  className="w-3 h-3 animate-spin" style={{ color: BRAND, flexShrink: 0 }} />
            }
            {/* Tool icon */}
            {Icon && <Icon className="w-3 h-3" style={{ color: BRAND, flexShrink: 0, opacity: 0.6 }} />}
            {/* Label */}
            <span style={{ fontSize: '0.7em', color: BRAND, fontWeight: 500, flexShrink: 0, opacity: step.done ? 0.6 : 1 }}>
              {meta?.label ?? step.name}
            </span>
            {/* Target arg */}
            {truncated && (
              <span style={{
                fontSize: '0.67em',
                color: 'rgba(255,255,255,0.3)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: 'monospace',
              }} title={target}>
                {truncated}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Citations Panel ───────────────────────────────────────────────────────────

function CitationsPanel({ sources }: { sources: string[] }) {
  if (!sources.length) return null;
  return (
    <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: `1px solid rgba(11,188,193,0.15)` }}>
      <div style={{
        fontSize: '0.62em',
        color: 'rgba(255,255,255,0.25)',
        marginBottom: '5px',
        textTransform: 'uppercase',
        letterSpacing: '0.1em',
        fontWeight: 600,
      }}>
        Sources
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {sources.map((src, i) => {
          const name = src.split(/[/\\]/).pop() || src;
          return (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '3px 8px',
              background: 'rgba(11,188,193,0.06)',
              border: '1px solid rgba(11,188,193,0.15)',
              borderRadius: '5px',
            }}>
              <span style={{
                background: BRAND,
                color: '#0d1520',
                width: '15px',
                height: '15px',
                borderRadius: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.58em',
                fontWeight: 700,
                flexShrink: 0,
              }}>{i + 1}</span>
              <span style={{
                fontSize: '0.7em',
                color: 'rgba(255,255,255,0.45)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: 'monospace',
              }} title={src}>{name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Widget ───────────────────────────────────────────────────────────────

export function ChatbotWidget() {
  const [enabled, setEnabled]     = useState(false);
  const [open, setOpen]           = useState(false);
  const [messages, setMessages]   = useState<Message[]>(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_MESSAGES_KEY);
      return saved ? (JSON.parse(saved) as Message[]).map(m => ({ ...m, streaming: false })) : [];
    } catch { return []; }
  });
  const [input, setInput]         = useState('');
  const [streaming, setStreaming] = useState(false);
  const messagesEndRef            = useRef<HTMLDivElement>(null);
  const abortRef                  = useRef<AbortController | null>(null);
  const textareaRef               = useRef<HTMLTextAreaElement>(null);
  const conversationIdRef         = useRef<number | null>(
    (() => { const s = sessionStorage.getItem(SESSION_CONV_ID_KEY); return s ? Number(s) : null; })()
  );

  useEffect(() => {
    const token = sessionStorage.getItem('acodoc_token');
    if (!token) return;
    fetch(`${API_BASE}/api/v1/rag/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then((d: { enabled: boolean }) => setEnabled(d.enabled))
      .catch(() => setEnabled(false));
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_MESSAGES_KEY, JSON.stringify(messages));
    } catch { /* quota exceeded – ignore */ }
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 80)}px`;
  }, [input]);

  if (!enabled) return null;

  const updateLastAssistant = (fn: (msg: Message) => Message) => {
    setMessages(prev => {
      const updated = [...prev];
      const idx = updated.length - 1;
      if (updated[idx]?.role === 'assistant') updated[idx] = fn(updated[idx]);
      return updated;
    });
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || streaming) return;

    setInput('');
    setMessages(prev => [
      ...prev,
      { role: 'user', content: text },
      { role: 'assistant', content: '', streaming: true },
    ]);
    setStreaming(true);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const resp = await fetch(`${API_BASE}/api/v1/rag/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('acodoc_token') || ''}`,
        },
        body: JSON.stringify({ message: text, conversation_id: conversationIdRef.current }),
        signal: ctrl.signal,
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      const handleEvent = (ev: SSEEvent) => {
        if (ev.type === 'conversation_id') {
          conversationIdRef.current = ev.id;
          sessionStorage.setItem(SESSION_CONV_ID_KEY, String(ev.id));
        } else if (ev.type === 'content') {
          updateLastAssistant(msg => ({ ...msg, content: msg.content + ev.text }));
        } else if (ev.type === 'tool') {
          const step: ToolStep = { id: ev.id, name: ev.name, args: ev.args, done: false };
          updateLastAssistant(msg => ({ ...msg, toolSteps: [...(msg.toolSteps ?? []), step] }));
        } else if (ev.type === 'tool_end') {
          updateLastAssistant(msg => ({
            ...msg,
            toolSteps: (msg.toolSteps ?? []).map(s => s.id === ev.id ? { ...s, done: true } : s),
          }));
        } else if (ev.type === 'citations') {
          updateLastAssistant(msg => ({ ...msg, citations: ev.sources }));
        } else if (ev.type === 'error') {
          updateLastAssistant(msg => ({ ...msg, content: msg.content + `\n\n⚠️ ${ev.message}` }));
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Split by \n\n (SSE event boundary)
        const events = buffer.split('\n\n');
        buffer = events.pop() ?? '';
        for (const event of events) {
          const line = event.split('\n').find(l => l.startsWith('data:'));
          if (line) handleEvent(parseSSELine(line));
        }
      }

      // Flush remaining buffer
      if (buffer) {
        const line = buffer.split('\n').find(l => l.startsWith('data:'));
        if (line) handleEvent(parseSSELine(line));
      }
    } catch (err: unknown) {
      if ((err as Error)?.name !== 'AbortError') {
        updateLastAssistant(msg => ({
          ...msg,
          content: msg.content || 'Désolé, une erreur est survenue.',
        }));
      }
    } finally {
      updateLastAssistant(msg => ({ ...msg, streaming: false }));
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const handleStop = () => {
    abortRef.current?.abort();
    updateLastAssistant(msg => ({ ...msg, streaming: false }));
    setStreaming(false);
  };

  const handleClose = () => {
    abortRef.current?.abort();
    setOpen(false);
  };

  const handleNewConversation = () => {
    abortRef.current?.abort();
    conversationIdRef.current = null;
    sessionStorage.removeItem(SESSION_CONV_ID_KEY);
    sessionStorage.removeItem(SESSION_MESSAGES_KEY);
    setMessages([]);
    setInput('');
    setStreaming(false);
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">

      {/* ── Chat Panel ───────────────────────────────────────────────── */}
      {open && (
        <div
          className="flex flex-col rounded-2xl shadow-2xl overflow-hidden animate-fade-up"
          style={{
            width: '380px',
            height: '520px',
            background: '#0d1520',
            border: `1px solid ${BRAND}25`,
            boxShadow: `0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px ${BRAND}15`,
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 shrink-0"
            style={{ background: `${BRAND}08`, borderBottom: `1px solid ${BRAND}18` }}
          >
            <div className="flex items-center gap-2.5">
              <div className="relative">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ background: `${BRAND}1A`, border: `1px solid ${BRAND}30` }}
                >
                  <MessageSquare className="w-3.5 h-3.5" style={{ color: BRAND }} />
                </div>
                <div
                  className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full animate-pulse"
                  style={{ background: BRAND, border: '1.5px solid #0d1520' }}
                />
              </div>
              <div>
                <div className="text-xs font-semibold leading-tight" style={{ color: BRAND }}>
                  ACODOC Assistant
                </div>
                <div className="text-xs leading-tight mt-0.5" style={{ color: 'rgba(255,255,255,0.22)' }}>
                  Recherche documentaire IA
                </div>
              </div>
            </div>
            {messages.length > 0 && !streaming && (
              <button
                onClick={handleNewConversation}
                className="p-1.5 rounded-lg hover:bg-white/5 transition"
                style={{ color: 'rgba(255,255,255,0.35)' }}
                title="Nouvelle conversation"
              >
                <SquarePen className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 chatbot-messages" style={{ gap: '12px', display: 'flex', flexDirection: 'column' }}>
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center flex-1 gap-3 px-4">
                <div
                  className="w-10 h-10 rounded-2xl flex items-center justify-center"
                  style={{ background: `${BRAND}12`, border: `1px solid ${BRAND}22` }}
                >
                  <MessageSquare className="w-5 h-5" style={{ color: BRAND }} />
                </div>
                <p className="text-xs text-center leading-relaxed" style={{ color: 'rgba(255,255,255,0.22)' }}>
                  Posez une question sur vos documents.<br />ACODOC Assistant vous répond.
                </p>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex animate-fade-up ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {/* Assistant avatar */}
                  {msg.role === 'assistant' && (
                    <div
                      className="w-5 h-5 rounded-md flex items-center justify-center shrink-0 mt-1 mr-2"
                      style={{ background: `${BRAND}12`, border: `1px solid ${BRAND}20` }}
                    >
                      <MessageSquare className="w-2.5 h-2.5" style={{ color: BRAND }} />
                    </div>
                  )}

                  <div
                    className="max-w-[82%] px-3 py-2.5 rounded-2xl"
                    style={
                      msg.role === 'user'
                        ? {
                            background: `${BRAND}18`,
                            border: `1px solid ${BRAND}30`,
                            borderBottomRightRadius: '4px',
                          }
                        : {
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.07)',
                            borderBottomLeftRadius: '4px',
                            flex: 1,
                          }
                    }
                  >
                    {msg.role === 'user' ? (
                      <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'rgba(255,255,255,0.9)' }}>
                        {msg.content}
                      </p>
                    ) : (
                      <>
                        {/* Tool steps — persistents, avec statut done/running */}
                        {msg.toolSteps && msg.toolSteps.length > 0 && (
                          <ToolStepsPanel steps={msg.toolSteps} />
                        )}

                        {/* Contenu markdown ou caret initial */}
                        {msg.content === '' && msg.streaming ? (
                          <span style={{
                            display: 'inline-block',
                            width: '7px',
                            height: '14px',
                            background: BRAND,
                            borderRadius: '1px',
                            animation: 'caret-blink 1s steps(1) infinite',
                          }} />
                        ) : msg.content ? (
                          <MarkdownContent content={msg.content} streaming={msg.streaming} />
                        ) : null}

                        {msg.citations && msg.citations.length > 0 && (
                          <CitationsPanel sources={msg.citations} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div
            className="px-3 pt-2.5 pb-3 shrink-0"
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div
              className="flex items-end gap-2 rounded-xl px-3 py-2 transition-all"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${BRAND}20`,
              }}
            >
              <textarea
                ref={textareaRef}
                rows={1}
                placeholder="Votre question…"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={streaming}
                className="flex-1 bg-transparent text-sm outline-none resize-none placeholder:text-white/20 disabled:opacity-40"
                style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.5, maxHeight: '80px' }}
              />
              {streaming ? (
                <button
                  onClick={handleStop}
                  className="w-7 h-7 flex items-center justify-center rounded-lg transition shrink-0 mb-0.5"
                  style={{
                    background: 'rgba(220,38,38,0.18)',
                    border: '1px solid rgba(220,38,38,0.3)',
                    color: '#f87171',
                  }}
                  title="Arrêter"
                >
                  <Square className="w-3 h-3" style={{ fill: '#f87171', stroke: 'none' }} />
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={!input.trim()}
                  className="w-7 h-7 flex items-center justify-center rounded-lg transition shrink-0 mb-0.5 hover:opacity-85"
                  style={{
                    background: input.trim() ? BRAND : 'rgba(255,255,255,0.06)',
                    color: input.trim() ? '#0d1520' : 'rgba(255,255,255,0.18)',
                  }}
                  title="Envoyer (Entrée)"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <p className="text-center mt-1.5" style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.14)' }}>
              Entrée pour envoyer · Maj+Entrée pour nouvelle ligne
            </p>
          </div>
        </div>
      )}

      {/* ── Floating Toggle Button ──────────────────────────────────── */}
      <button
        onClick={() => (open ? handleClose() : setOpen(true))}
        className="w-12 h-12 rounded-full flex items-center justify-center shadow-xl transition-all hover:scale-105 active:scale-95"
        style={{
          background: open ? 'rgba(11,188,193,0.12)' : BRAND,
          border: `2px solid ${open ? BRAND : 'transparent'}`,
          color: open ? BRAND : '#0d1520',
          boxShadow: open ? 'none' : `0 8px 24px ${BRAND}50`,
        }}
        title={open ? 'Fermer' : 'ACODOC Assistant'}
      >
        {open ? <X className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
      </button>
    </div>
  );
}
